/**
 * Seeding a filter's TOP-LEVEL channels into the group tree the editors render.
 *
 * Every filter-hosting surface (the level inspector, segment panel,
 * applicability editor, related-spheres editor, the advanced dialog) mounts
 * `UnifiedFilterTreeMolecule` against a single canonical `FilterGroup` root,
 * but the `FilterState` it owns still carries top-level `propertyFilters`
 * alongside `filterGroups`. Folding one into the other is the same operation
 * on every surface, so it lives here once.
 */
import type { FilterExpression, FilterGroupLeaf } from 'types/filters';
import { isLaneBackedPropertyName } from '@center-platform/spheres/filters';
import type { SimpleFilterGroup } from '../types/filterState';
import { nextFilterId } from './ids';

/**
 * Converts a TOP-LEVEL row into the canonical leaf it enters the tree as.
 *
 * `unifiedToFilterState` still surfaces a stored top-level
 * `arcFilters`/`relationshipFilters`/`perspectiveFilters` lane as a
 * `{property:'arc'|…, value:<filter>}` row so the surface's own top-level
 * channel round-trips (SC7 top-level hoist). The tree's model is the canonical
 * `FilterGroupLeaf` union, so that row is translated ONCE here, on the way in —
 * a raw lane-backed property row must never reach a group, where an engine
 * would read it as an ordinary comparison and widen on it (never-widen).
 * A plain property row passes through unchanged.
 */
export function topLevelRowToLeaf(row: FilterExpression): FilterGroupLeaf {
  if (!isLaneBackedPropertyName(row.property)) return row;
  const value = (row as { value?: unknown }).value;
  const raw = value != null && typeof value === 'object' ? (value as { id?: string }) : {};
  // The row's identity belongs on the payload — that is where a lane leaf keeps
  // it (see `withStableFilterGroupIds`), so carry it down rather than dropping
  // it and having the leaf re-minted with a different id on the next render.
  const filter = row.id && raw.id === undefined ? { ...raw, id: row.id } : raw;
  if (row.property === 'arc') return { kind: 'arc', filter } as FilterGroupLeaf;
  if (row.property === 'perspective') return { kind: 'perspective', filter } as FilterGroupLeaf;
  return { kind: 'relationship', filter } as FilterGroupLeaf;
}

/**
 * Folds `leaves` into `groups`, returning the tree the editor renders.
 *
 * Top-level channels are AND-combined with the group tree, so the leaves join
 * the first group only when that group is itself an AND. When the root is an
 * OR they get their own leading AND group instead — merging them into the OR
 * would turn `leaf AND (a OR b)` into `(leaf OR a OR b)`, a widening.
 *
 * The fallback/leading group mints its `id` here, at construction: callers own
 * raw state and re-enter with it, so an id-less group would be re-minted
 * downstream on every pass and remount the subtree on each keystroke.
 */
export function seedLeavesIntoGroups(
  groups: SimpleFilterGroup[],
  leaves: FilterGroupLeaf[]
): SimpleFilterGroup[] {
  const baseGroups: SimpleFilterGroup[] =
    groups.length > 0 ? groups : [{ id: nextFilterId(), operator: 'and', filters: [] }];
  if (leaves.length === 0) return baseGroups;
  if (baseGroups[0].operator === 'and') {
    return baseGroups.map((g, i) => (i === 0 ? { ...g, filters: [...g.filters, ...leaves] } : g));
  }
  return [{ id: nextFilterId(), operator: 'and', filters: leaves }, ...baseGroups];
}
